import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Receipt, Zap, X, Package, Clock, AlertCircle, Euro, TrendingUp, TrendingDown, CreditCard, FileText, Plus } from '../../icons/wp-icons';
import { createGesamtrechnung } from '../../api/projektApi';
import toast from 'react-hot-toast';

export default function ProjektGesamtrechnung({ projektId, projekt, statistiken, onRechnungCreated }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDifferenzModal, setShowDifferenzModal] = useState(false);
  const [options, setOptions] = useState({
    modus: 'AUTO',
    inkludiereMaterialien: true,
    inkludiereZeiterfassung: true
  });

  const handleCreate = async () => {
    setLoading(true);
    try {
      const rechnung = await createGesamtrechnung(projektId, options);
      toast.success('Gesamtrechnung erstellt - Sie können sie jetzt bearbeiten');
      setShowModal(false);
      if (onRechnungCreated) onRechnungCreated();
      // Zur Bearbeitungsseite weiterleiten
      navigate(`/rechnungen/${rechnung.id}/bearbeiten`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format((cents || 0) / 100);
  };

  const materialVerbaut = statistiken?.material?.verbaut || 0;
  const hasPositions = materialVerbaut > 0 || (statistiken?.arbeit?.eintraege || 0) > 0;

  // Finanz-Berechnungen
  const budget = statistiken?.budget?.budget || projekt?.budgetNetto || 0;
  const rechnungGesamt = statistiken?.finanzen?.rechnungGesamt || 0;
  const bezahlt = statistiken?.finanzen?.bezahlt || 0;
  const offen = statistiken?.finanzen?.offen || 0;
  const gesamtKosten = statistiken?.gesamtKosten || 0;

  // Differenz zwischen Budget und tatsächlichen Kosten
  const budgetDifferenz = budget > 0 ? budget - gesamtKosten : 0;

  // Differenz zwischen bezahlt und tatsächlichen Kosten (für Gutschrift/Nachzahlung)
  const zahlungsDifferenz = bezahlt - gesamtKosten;

  return (
    <>
      <div className="bg-surface rounded-card shadow-card p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2 text-text">
          <Receipt className="w-5 h-5 text-text-muted" />
          Projekt-Gesamtrechnung
        </h3>

        <div className="space-y-3 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-text-muted flex items-center gap-2">
              <Package className="w-4 h-4" />
              Material (VK)
            </span>
            <div className="text-right">
              <span className="font-medium text-text">{formatCurrency(statistiken?.material?.kostenVerbaut || 0)}</span>
              <span className="text-xs text-text-muted block">
                {statistiken?.material?.verbaut || 0} von {statistiken?.material?.anzahl || 0} verbaut
              </span>
            </div>
          </div>
          {(statistiken?.material?.einkaufskosten > 0) && (
            <div className="flex justify-between items-center pl-6">
              <span className="text-text-muted text-sm">Material (EK)</span>
              <div className="text-right">
                <span className="font-medium text-text">{formatCurrency(statistiken?.material?.einkaufskosten)}</span>
                <span className="text-xs text-green-600 block">
                  Marge: {formatCurrency(statistiken?.material?.marge || 0)}
                </span>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-text-muted flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Arbeitskosten
            </span>
            <div className="text-right">
              <span className="font-medium text-text">{formatCurrency(statistiken?.arbeit?.kosten)}</span>
              <span className="text-xs text-text-muted block">
                {statistiken?.arbeit?.stunden?.toFixed(1) || 0} Stunden
              </span>
            </div>
          </div>
          <div className="flex justify-between pt-3 border-t border-border">
            <span className="font-semibold text-text">Gesamt VK (Netto)</span>
            <span className="font-semibold text-text">{formatCurrency(gesamtKosten)}</span>
          </div>
          {(statistiken?.material?.einkaufskosten > 0) && (
            <>
              <div className="flex justify-between items-center text-sm">
                <span className="text-text-muted">Gesamt EK (Material)</span>
                <span className="text-text">{formatCurrency(statistiken?.material?.einkaufskosten)}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-border">
                <span className="font-semibold text-green-700">Rohertrag</span>
                <span className="font-semibold text-green-700">
                  {formatCurrency((statistiken?.material?.kostenVerbaut || 0) + (statistiken?.arbeit?.kosten || 0) - (statistiken?.material?.einkaufskosten || 0))}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Budget-Vergleich wenn vorhanden */}
        {budget > 0 && (
          <div className={`mb-4 p-3 rounded-button ${budgetDifferenz >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="flex justify-between items-center">
              <span className="text-sm text-text-muted flex items-center gap-1">
                <Euro className="w-4 h-4" />
                Budget
              </span>
              <span className="font-medium text-text">{formatCurrency(budget)}</span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <span className="text-sm text-text-muted">Differenz</span>
              <span className={`font-medium flex items-center gap-1 ${budgetDifferenz >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {budgetDifferenz >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {budgetDifferenz >= 0 ? '+' : ''}{formatCurrency(budgetDifferenz)}
              </span>
            </div>
          </div>
        )}

        {/* Zahlungs-Status */}
        {rechnungGesamt > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-button">
            <div className="flex justify-between items-center">
              <span className="text-sm text-text-muted flex items-center gap-1">
                <CreditCard className="w-4 h-4" />
                Bereits abgerechnet
              </span>
              <span className="font-medium text-text">{formatCurrency(rechnungGesamt)}</span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <span className="text-sm text-text-muted">Davon bezahlt</span>
              <span className="font-medium text-green-600">{formatCurrency(bezahlt)}</span>
            </div>
            {offen > 0 && (
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm text-text-muted">Noch offen</span>
                <span className="font-medium text-orange-600">{formatCurrency(offen)}</span>
              </div>
            )}
          </div>
        )}

        <button
          onClick={() => setShowModal(true)}
          disabled={!hasPositions}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-button hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Zap className="w-5 h-5" />
          Gesamtrechnung erstellen
        </button>

        {!hasPositions && (
          <p className="text-xs text-text-muted mt-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Keine verbauten Materialien oder Zeiterfassungen vorhanden
          </p>
        )}

        {/* Differenz-Ausgleich (Gutschrift/Nachzahlung) */}
        {bezahlt > 0 && gesamtKosten > 0 && Math.abs(zahlungsDifferenz) > 100 && (
          <div className={`mt-4 p-4 rounded-button border-2 ${
            zahlungsDifferenz > 0
              ? 'bg-blue-50 border-blue-300'
              : 'bg-orange-50 border-orange-300'
          }`}>
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${zahlungsDifferenz > 0 ? 'bg-blue-100' : 'bg-orange-100'}`}>
                <FileText className={`w-5 h-5 ${zahlungsDifferenz > 0 ? 'text-blue-600' : 'text-orange-600'}`} />
              </div>
              <div className="flex-1">
                <h4 className={`font-medium ${zahlungsDifferenz > 0 ? 'text-blue-800' : 'text-orange-800'}`}>
                  {zahlungsDifferenz > 0 ? 'Gutschrift empfohlen' : 'Nachzahlung erforderlich'}
                </h4>
                <p className={`text-sm mt-1 ${zahlungsDifferenz > 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                  {zahlungsDifferenz > 0
                    ? `Der Kunde hat ${formatCurrency(zahlungsDifferenz)} mehr bezahlt als die tatsächlichen Kosten.`
                    : `Die tatsächlichen Kosten übersteigen die bezahlten Beträge um ${formatCurrency(Math.abs(zahlungsDifferenz))}.`
                  }
                </p>
                <div className="mt-3 flex gap-2">
                  {zahlungsDifferenz > 0 ? (
                    <Link
                      to={`/rechnungen/erstellen?kundeId=${projekt?.kundeId}&typ=gutschrift&betrag=${Math.abs(zahlungsDifferenz)}&projektId=${projektId}&beschreibung=Gutschrift%20f%C3%BCr%20Projekt`}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-white rounded-button hover:bg-primary-dark transition"
                    >
                      <Plus className="w-4 h-4" />
                      Gutschrift erstellen
                    </Link>
                  ) : (
                    <Link
                      to={`/rechnungen/erstellen?kundeId=${projekt?.kundeId}&betrag=${Math.abs(zahlungsDifferenz)}&projektId=${projektId}&beschreibung=Nachzahlung%20f%C3%BCr%20Projekt`}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-warning text-white rounded-button hover:bg-amber-700 transition"
                    >
                      <Plus className="w-4 h-4" />
                      Nachzahlung erstellen
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-card p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-text">Gesamtrechnung erstellen</h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-surface-elevated rounded-button">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.inkludiereMaterialien}
                  onChange={(e) => setOptions({ ...options, inkludiereMaterialien: e.target.checked })}
                  className="w-4 h-4 text-primary mt-0.5"
                />
                <div>
                  <span className="text-text font-medium">Verbaute Materialien einbeziehen</span>
                  <span className="block text-sm text-text-muted">
                    {statistiken?.material?.verbaut || 0} Positionen = {formatCurrency(statistiken?.material?.kosten)}
                  </span>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.inkludiereZeiterfassung}
                  onChange={(e) => setOptions({ ...options, inkludiereZeiterfassung: e.target.checked })}
                  className="w-4 h-4 text-primary mt-0.5"
                />
                <div>
                  <span className="text-text font-medium">Offene Zeiterfassungen einbeziehen</span>
                  <span className="block text-sm text-text-muted">
                    {statistiken?.arbeit?.eintraege || 0} Einträge = {formatCurrency(statistiken?.arbeit?.kosten)}
                  </span>
                </div>
              </label>
            </div>

            <div className="bg-surface-elevated rounded-button p-4 mb-6">
              <p className="text-sm text-text-muted">
                Es wird eine neue Rechnung im Status "Entwurf" mit allen ausgewählten Positionen erstellt.
                Zeiterfassungen werden als "abgerechnet" markiert.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-border rounded-button hover:bg-surface-elevated transition"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCreate}
                disabled={loading || (!options.inkludiereMaterialien && !options.inkludiereZeiterfassung)}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition disabled:opacity-50"
              >
                {loading ? 'Erstelle...' : 'Rechnung erstellen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
